import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from '../../components/ConfirmModal';
import formaPagamentoService from '../../services/formaPagamentoService';
import Toast from '../../components/Toast';

const initialState = {
  nome: '',
  status: 'Ativo',
};

const FormaPagamentoEdit = () => {
  const { id } = useParams();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const navigate = useNavigate();

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    const loadFormaPagamento = async () => {
      try {
        console.log('Carregando forma de pagamento, ID:', id);
        const response = await formaPagamentoService.getById(id);
        console.log('Resposta da API:', response);

        // Verificação defensiva para diferentes estruturas de resposta
        let pagamento = null;
        if (response) {
          if (response.success && response.data) {
            pagamento = response.data;
          } else if (response.data) {
            pagamento = response.data;
          } else if (Array.isArray(response)) {
            pagamento = response.find(f => String(f.idFormaPagamento) === String(id));
          } else if (response.idFormaPagamento) {
            pagamento = response;
          }
        }

        console.log('Dados do pagamento processados:', pagamento);
        if (pagamento) {
          setForm({ nome: pagamento.nome || '', status: pagamento.status || 'Ativo' });
        } else {
          setError('Forma de pagamento não encontrada');
        }
      } catch (error) {
        console.error('Erro ao carregar forma de pagamento:', error);
        const errorMessage = error.response?.data?.message || 'Erro ao carregar forma de pagamento';
        setError(errorMessage);
        showToast(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadFormaPagamento();
    }
  }, [id]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setError('');
    // Limpar erro do campo específico
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: '' });
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      setShowConfirm(false);

      await formaPagamentoService.update(id, form);

      setSuccess(true);
      showToast('Forma de pagamento atualizada com sucesso!');
      setTimeout(() => {
        navigate('/forma-pagamento');
      }, 2000);
    } catch (error) {
      console.error('Erro ao atualizar forma de pagamento:', error);
      const errorData = error.response?.data;
      
      // Se houver erros de validação específicos dos campos
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        const errors = {};
        errorData.errors.forEach(error => {
          if (error.path) {
            if (!errors[error.path]) {
              errors[error.path] = error.msg;
            }
          }
        });
        setFieldErrors(errors);
      }
      
      const errorMessage = errorData?.message || 'Erro ao atualizar forma de pagamento';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setShowDeleteConfirm(false);

      await formaPagamentoService.delete(id);

      setDeleteSuccess(true);
      showToast('Forma de pagamento excluída com sucesso!');
      setTimeout(() => {
        navigate('/forma-pagamento');
      }, 2000);
    } catch (error) {
      console.error('Erro ao excluir forma de pagamento:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao excluir forma de pagamento';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full bg-white rounded shadow p-6 mt-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (deleteSuccess) {
    return (
      <div className="w-full bg-white rounded shadow p-6 mt-6">
        <div className="text-center py-8">
          <div className="text-green-600 text-4xl mb-4">✓</div>
          <h2 className="text-xl font-bold text-green-600 mb-2">Forma de Pagamento Excluída</h2>
          <p className="text-gray-600">Redirecionando para a listagem...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Editar Forma de Pagamento</h2>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          Forma de pagamento atualizada com sucesso!
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Nome *</label>
          <input
            type="text"
            name="nome"
            value={form.nome}
            onChange={handleChange}
            maxLength={50}
            className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${fieldErrors.nome ? 'border-red-500' : ''}`}
            placeholder="Nome da forma de pagamento"
          />
          {fieldErrors.nome && <p className="text-red-500 text-xs mt-1">{fieldErrors.nome}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select
            name="status"
            value={form.status}
            onChange={handleChange}
            className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${fieldErrors.status ? 'border-red-500' : ''}`}
          >
            <option value="Ativo">Ativo</option>
            <option value="Inativo">Inativo</option>
          </select>
          {fieldErrors.status && <p className="text-red-500 text-xs mt-1">{fieldErrors.status}</p>}
        </div>

        {error && <div className="text-red-600 text-sm">{error}</div>}
        {loading && <div className="text-blue-600 text-sm">Salvando...</div>}
        {success && <div className="text-green-600 text-sm">Forma de pagamento atualizada com sucesso!</div>}

        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button type="button" className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center" onClick={() => navigate('/forma-pagamento')}>
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button type="submit" className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center" disabled={loading} aria-label="Salvar">
            <FontAwesomeIcon icon={faSave} />
            <span className="hidden sm:inline">{loading ? 'Salvando...' : 'Salvar'}</span>
          </button>
          <button type="button" className="p-2 sm:px-4 sm:py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-700 flex items-center gap-2 w-10 sm:w-auto justify-center" onClick={() => setShowDeleteConfirm(true)} disabled={deleting}>
            <FontAwesomeIcon icon={faTrash} />
            <span className="hidden sm:inline">{deleting ? 'Excluindo...' : 'Excluir'}</span>
          </button>
        </div>
      </form>

      <ConfirmModal
        isOpen={showConfirm}
        title="Confirmar Atualização"
        message="Tem certeza que deseja atualizar esta forma de pagamento?"
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
      />

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir esta forma de pagamento? Esta ação não pode ser desfeita."
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmText="Confirmar"
        confirmColor="red"
        danger
      />

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: '', type: 'success' })}
      />
    </div>
  );
};

export default FormaPagamentoEdit;